/**
 * =============================================================================================================================
 * This service allows you to get information from the Entity Config (EC).
 * It exposes the EntityConfigService and allows you to use a promise based approach to get data
 * It will help you, the developer, get information from the EC in a format that is easy to work with.
 *
 * Only use this Service! Everything you need should be here and if it is not, feel free to expand it! <3
 * =============================================================================================================================
 */

import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FeatureFlagService, PermissionService, PermissionVerbsV1, PermissionVerbsV2 } from '@innovation-os-ng/core/core';
import { Tenant } from '@innovation-os-ng/core/models';
import {
  ControlPropertyValue,
  EntityConfigControl,
  EntityConfigControlChild,
  EntityConfigField,
  EntityConfigType,
  FeatureFlags,
  PropertySuffix
} from '@innovation-os-ng/core/shared';
import { TenantSelectors, skipper } from '@innovation-os-ng/core/state';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { EntityConfigFieldTypePropertyUri, EntityConfigFieldTypePropertyValueMapper } from '../model/fieldTypePropertyUris';
import { EntityConfigRelationType } from '../model/relationType';
import { EntityConfigRelationTypeService } from './entity-config-relation-type.service';
import { EntityConfigService } from './entity-config.service';

@Injectable({
  providedIn: 'root'
})
export class EntityConfigHelperService {
  private tenantUri: string;

  // Capped at 5 generations to prevent broken application.
  private readonly MAX_GENERATION_CAP = 5;
  private useWorkspaceLevelEC: boolean;

  constructor(
    private readonly entityConfigService: EntityConfigService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly entityConfigRelationTypeService: EntityConfigRelationTypeService,
    private permissionService: PermissionService,
    private readonly store: Store
  ) {
    // Prefetch TenantUri if possible
    this.store
      .select(TenantSelectors.selectCurrentTenant)
      .pipe(skipper(), takeUntilDestroyed())
      .subscribe((tenant: Tenant) => {
        this.tenantUri = tenant.tenantUri;
      });
    this.featureFlagService
      .isFeatureEnabled(FeatureFlags.WORKSPACE_LEVEL_ELEMENT_CONFIG)
      .then((enabled: boolean) => (this.useWorkspaceLevelEC = enabled));
  }

  /**
   * This service looks up the current tenant by itself
   * Use this param to overwrite the "globally" used tenant which all other getters depend on.
   *
   * @param tenantUri: string
   */
  public setTenantUri(tenantUri: string) {
    this.tenantUri = tenantUri;
  }

  /**
   * WARNING: If you are sure that tenantUri is known to this service, use it
   * If you want to be in safer side, use getTenantUriPromise() instead
   *
   * @returns string
   */
  public getTenantUri() {
    return this.tenantUri;
  }

  // /**
  //  * It promises to return the tenantUri, however if it failed, it will return an empty string
  //  *
  //  * @returns Promise<string>
  //  */
  // public getTenantUriPromise(): Promise<string> {
  //   if (this.tenantUri) {
  //     return Promise.resolve(this.tenantUri);
  //   } else {
  //     return this.tenantService.getCurrentTenant().then((tenant) => {
  //       if (this.tenantUri) {
  //         // if it is set manually, we just provide it
  //         return this.tenantUri;
  //       }
  //       this.tenantUri = tenant ? tenant.tenantUri : '';
  //       return this.tenantUri;
  //     });
  //   }
  // }

  // ======================================= Getters for High Level Data ======================================= //

  /**
   * @deprecated This method is deprecated. Please use getAllElementTypes method from ElementConfigStateService instead.
   *
   * Get all Element Types - Use this to get an Array of ElementTypes
   *
   * @returns Promise<Array<EntityConfigType>>
   */
  public async getAllElementTypes(spaceUri?: string): Promise<Array<EntityConfigType>> {
    const tenant = await firstValueFrom(this.store.select(TenantSelectors.selectCurrentTenant).pipe(skipper()));
    this.tenantUri = this.tenantUri || tenant.tenantUri;
    console.assert(!!this.tenantUri, 'tenant should not be null');
    return this.entityConfigService.getElementTypes(this.tenantUri, spaceUri).pipe(take(1)).toPromise();
  }

  /**
   * @deprecated This method is deprecated. Please use getAllControls method from ElementConfigStateService instead.
   *
   * Get all Controls - Use this to get an Array of Controls
   *
   * @returns Promise<Array<EntityConfigControl>>
   */
  public async getAllControls(spaceUri?: string): Promise<Array<EntityConfigControl>> {
    const tenant = await firstValueFrom(this.store.select(TenantSelectors.selectCurrentTenant).pipe(skipper()));
    this.tenantUri = this.tenantUri || tenant.tenantUri;
    console.assert(!!this.tenantUri, 'tenant should not be null');
    return this.entityConfigService.getControls(this.tenantUri, spaceUri).pipe(take(1)).toPromise();
  }

  /**
   * @deprecated This method is deprecated. Please use getAllFields method from ElementConfigStateService instead.
   *
   * Get all Fields - use this to get an Array of Feilds
   *
   * @returns Promise<Array<EntityConfigField>>
   */
  public async getAllFields(spaceUri?: string): Promise<Array<EntityConfigField>> {
    const tenant = await firstValueFrom(this.store.select(TenantSelectors.selectCurrentTenant).pipe(skipper()));
    this.tenantUri = this.tenantUri || tenant.tenantUri;
    console.assert(!!this.tenantUri, 'tenant should not be null');
    return this.entityConfigService.getFields(this.tenantUri, spaceUri).pipe(take(1)).toPromise();
  }

  /**
   * @deprecated This method is deprecated. Please use getAllRelationTypes method from ElementConfigStateService instead.
   *
   * Get all Relation Types - use this to get an Array of Relation Types
   *
   * @param spaceUri string
   * @returns Promise<Array<EntityConfigType>>
   */
  public async getAllRelationTypes(spaceUri?: string): Promise<Array<EntityConfigRelationType>> {
    console.assert(!!this.tenantUri, 'tenant should not be null');
    if (this.useWorkspaceLevelEC) {
      return firstValueFrom(this.entityConfigRelationTypeService.getRelationTypes());
    } else {
      return firstValueFrom(this.entityConfigService.getRelationTypes(this.tenantUri, spaceUri));
    }
  }

  // ======================================= Getters for Low Level Data ======================================= //

  /**
   * @deprecated This method is deprecated. Please use getElementType method from ElementConfigStateService instead.
   *
   * Get a single ElementType - Use this if you already know the ElementType uri and want to fetch a specific ElementType
   *
   * @param elementTypeUri
   * @returns Promise<EntityConfigType>
   */
  public getElementType(elementTypeUri: string, spaceUri?: string): Promise<EntityConfigType> {
    return this.getAllElementTypes(spaceUri).then((elementTypes) => {
      if (elementTypes) {
        // Resolve Data
        return elementTypes.find((type) => type.uri === elementTypeUri);
      } else {
        // Reject Data
        return Promise.reject();
      }
    });
  }

  /**
   * @deprecated This method is deprecated. Please use getField method from ElementConfigStateService instead.
   *
   * Get a single Field - Use this if you already know the field Uri and want to fetch a specific field
   *
   * @param fieldUri string
   * @returns Promise<EntityConfigField>
   */
  public getField(fieldUri: string, spaceUri?: string): Promise<EntityConfigField> {
    return this.getAllFields(spaceUri).then((fields) => {
      if (fields) {
        // Resolve Data
        return fields.find((field) => field.uri === fieldUri);
      } else {
        // Reject Data
        return Promise.reject();
      }
    });
  }

  /**
   * @deprecated This method is deprecated. Please use getControl method from ElementConfigStateService instead.
   *
   * Get a single Control - Use this if you already know the Control uri and want to fetch a specific Control
   *
   * @param controlUri string
   */
  public getControl(controlUri: string, spaceUri?: string): Promise<EntityConfigControl> {
    return this.getAllControls(spaceUri).then((controls) => {
      if (controls) {
        // Resolve Data
        return controls.find((control) => control.uri === controlUri);
      } else {
        // Reject Data
        return Promise.reject();
      }
    });
  }

  /**
   * @deprecated This method is deprecated. Please use getPageControlsByElementType method from ElementConfigStateService instead.
   *
   * Get the parent controls from a Entity Type - this is the first entry into the EC that can be used to start rendering
   * the Entity Detail Page
   *
   * @param elementTypeUri string
   */
  public getPageControlsByElementType(elementTypeUri: string, spaceUri?: string): Promise<Array<EntityConfigControl>> {
    return this.getElementType(elementTypeUri, spaceUri)
      .then((type) => (type ? type.elementControlUris : []))
      .then((elementControlUris) => Promise.all(elementControlUris.map((uri) => this.getControl(uri, spaceUri))))
      .then((controls) => controls.filter((c) => !!c));
  }

  /**
   * @deprecated This method is deprecated. Please use getRelationType method from ElementConfigStateService instead.
   *
   * Get a single RelationType - Use this if you already know the RelationType uri and want to fetch a specific RelationType
   *
   * @param spaceUri: string
   * @param relationTypeUri string
   */
  public getRelationType(spaceUri: string, relationTypeUri: string): Promise<EntityConfigRelationType> {
    return this.getAllRelationTypes(spaceUri).then((relationTypes) => {
      if (relationTypes) {
        // Resolve Data
        return relationTypes.find((relationType) => relationType.uri === relationTypeUri);
      } else {
        // Reject Data
        return Promise.reject();
      }
    });
  }

  /**
   * @deprecated This method is deprecated. Please use getFieldUrisForElementType method from getRelationType instead.
   *
   * Retrieves the URIs of fields for specified element types.
   *
   * @param {string} elementTypeUri - The URI of the element type for which fields URIs are to be retrieved.
   * @param {boolean} [includeMandatory=true] - Indicates whether to include mandatory fields in the result. Defaults to true.
   * @param {boolean} [includeHidden=true] - Indicates whether to include hidden fields in the result. Defaults to true.
   * @param {string} [spaceUri] - Optional. The URI of the space from which to retrieve fields.
   * @returns {Promise<Array<string>>} A promise resolving to an array of field URIs.
   */
  public getFieldUrisForElementType(
    elementTypeUri: string,
    includeMandatory: boolean = true,
    includeHidden: boolean = true,
    spaceUri?: string
  ): Promise<Array<string>> {
    return this.getFieldsForElementType(elementTypeUri, includeMandatory, includeHidden, spaceUri).then(
      (fields: Array<EntityConfigField>) => fields.map((field: EntityConfigField) => field.uri)
    );
  }

  /**
   * @deprecated This method is deprecated. Please use getFieldsForElementType method from ElementConfigStateService instead.
   *
   * Get all Fields associated with a single ElementType
   *
   * @param elementTypeUri string - the type URI of the ElementType to request Fields for
   */
  public getFieldsForElementType(
    elementTypeUri: string,
    includeMandatory: boolean = true,
    includeHidden: boolean = true,
    spaceUri?: string
  ): Promise<Array<EntityConfigField>> {
    return Promise.all([this.getElementType(elementTypeUri, spaceUri), this.getAllControls(spaceUri), this.getAllFields(spaceUri)]).then(
      ([type, controls, fields]) => {
        if (!type) {
          return [];
        }

        const mandatoryFields = (fields || []).filter((field) => EntityConfigField.isMandatory(field));
        return (
          type.elementControlUris
            // map uris to top level controls
            .map((controlUri) => (controls || []).find((control) => control.uri === controlUri))
            // we filter out the not found controls
            .filter((control) => !!control)
            // get all control descendants of all top level controls
            .reduce((all: Array<EntityConfigControlChild>, control) => all.concat(control.descendants), [])

            //  Filter out the hidden controls or the controls that have a parent hidden => disabled: true
            .filter((controlChild) => {
              // We need to find grandParent to know if the parent of this control is disabled
              const grandParent = controls.find(
                (control) =>
                  control.descendants.findIndex((d) => d.uri === controlChild.parentControlUri && d.parentControlUri === control.uri) !== -1
              );

              const parent = (grandParent ? grandParent.descendants : []).find((d) => d.uri === controlChild.parentControlUri);

              //  we filter out the disabled controls or controls with parent disabled
              return includeHidden ? true : !controlChild.disabled && parent && !parent.disabled;
            })
            // since those descendants are not complete in terms of info, map to controls
            .map((controlChild) => (controls || []).find((control) => control.uri === controlChild.uri))
            // keep only those with a field and not undefined
            .filter((control) => !!control?.field)
            // get the fields
            .map((control) => control.field)
            // include mandatory properties which aren't necessarily available in the control hierarchy?
            .concat(includeMandatory ? mandatoryFields : [])
            // get rid of duplicates
            .filter((field, index, array) => array.findIndex((other) => other.uri === field.uri) === index)
        );
      }
    );
  }

  /**
   * @deprecated This method is deprecated. Please use getLeafControlsForElementType method from ElementConfigStateService instead.
   *
   * Get all Controls associated with a single ElementType
   *
   * @param elementTypeUri string - the type URI of the ElementType to request Controls for
   */
  public getLeafControlsForElementType(
    elementTypeUri: string,
    spaceUri?: string,
    includeHidden: boolean = true
  ): Promise<Array<EntityConfigControl>> {
    return Promise.all([this.getElementType(elementTypeUri, spaceUri), this.getAllControls(spaceUri)]).then(([type, controls]) => {
      if (!type) {
        return [];
      }

      return (
        type.elementControlUris
          // map uris to top level controls
          .map((controlUri) => (controls || []).find((control) => control.uri === controlUri))
          // we filter out the not found controls
          .filter((control) => !!control)
          // get all control descendants of all top level controls
          .reduce((all: Array<EntityConfigControlChild>, control) => all.concat(control.descendants), [])

          //  Filter out the hidden controls or the controls that have a parent hidden => disabled: true
          .filter((controlChild) => {
            // We need to find grandParent to know if the parent of this control is disabled
            const grandParent = controls.find(
              (control) =>
                control.descendants.findIndex((d) => d.uri === controlChild.parentControlUri && d.parentControlUri === control.uri) !== -1
            );

            const parent = (grandParent ? grandParent.descendants : []).find((d) => d.uri === controlChild.parentControlUri);

            //  we filter out the disabled controls or controls with parent disabled
            return includeHidden ? true : !controlChild.disabled && parent && !parent.disabled;
          })

          // since those descendants are not complete in terms of info, map to controls
          .map((controlChild) => (controls || []).find((control) => control.uri === controlChild.uri))
          // keep only those with a field and not undefined
          .filter((control) => !!control?.field)
          // get rid of duplicates
          .filter((control, index, array) => array.findIndex((other) => other.uri === control.uri) === index)
      );
    });
  }

  public getFieldPropertyValue(field: EntityConfigField, property: EntityConfigFieldTypePropertyUri) {
    const propertyDefinition = field.propertyValues.find((prop) => prop.fieldTypePropertyUri === property);
    return propertyDefinition ? EntityConfigFieldTypePropertyValueMapper[property](propertyDefinition.value) : undefined;
  }

  /**
   * Get feature flag property
   */
  public getFeatureFlagProperties(control: EntityConfigControl): Array<ControlPropertyValue> {
    return this.getControlProperties(control, PropertySuffix.FEATURE_FLAG);
  }

  /**
   * Get a list of properties with a suffix
   */
  public getControlProperties(control: EntityConfigControl, suffix: PropertySuffix): Array<ControlPropertyValue> {
    return control?.propertyValues.filter((property: ControlPropertyValue) => property.controlTypePropertyUri.endsWith(suffix));
  }

  /**
   *  Check whether the control is disable/enable through feature flag
   */
  public isControlEnabledByFFlag(control: EntityConfigControl, allEnabledFeatureFlags): boolean {
    const featureFlags = this.getFeatureFlagProperties(control);
    if (featureFlags && !featureFlags.every((fflag) => allEnabledFeatureFlags.includes(fflag.value))) {
      return false;
    }
    return true;
  }

  /* Checks first whether this control is feature flagged, then checks whether it is disabled by user for given element type.
   *
   *  @param control
   *  @param elementType
   *  @param allControls              Mostly all controls in this workspace. Something like the response of getAllControls()
   *  @param allEnabledFeatureFlags   List of all feature flags enabled for the tenant.
   *                                  We don't need to add async and feature flag dependency for this method.
   */
  public isControlAvailableForElementType(
    control: EntityConfigControl,
    elementType: EntityConfigType,
    allControls: Array<EntityConfigControl>,
    allEnabledFeatureFlags: Array<string>
  ): boolean {
    // We check whether this control is feature flagged.
    if (!this.isControlEnabledByFFlag(control, allEnabledFeatureFlags)) {
      return false;
    }

    const allDescendantsFlat = this.allDescendantsFlat(allControls);
    /**
     * Get all previous generations of this control for this elementType.
     * A control can be reused in multiple other controls as a descendant.
     * Here the previous generation of this control will be these other controls.
     * Control A --> Descendant of Control B and Control C
     * Control B --> Descendant of Control D
     * Control C --> Descendant of Control E
     * All previous generations of Control A = [[Control B, Control D], [Control C, Control E]]
     * */
    const allPreviousGenerations = this.getGenerationsOf(control.uri, allDescendantsFlat).find((generationSet) =>
      // Consider this element has elementControlUri - Control E, then we found a proper generation tree of Control A
      elementType.elementControlUris.includes(generationSet[generationSet.length - 1])
    );
    // If we have allPreviousGenerations for this control, we check further whether any of the parent controls is disabled
    if (allPreviousGenerations) {
      // We check if any of the parent is disabled.
      // NOTE: Control can only be disabled when it is a descendant of a control.
      const anyParentDisabled = allPreviousGenerations.some(
        (parentUri) => allDescendantsFlat.find((descendant) => descendant.uri === parentUri)?.disabled ?? false
      );
      // If any parent is disabled, we need to hide this control.
      if (anyParentDisabled) {
        return false;
      }
    }
    return true;
  }

  /**
   * Get all previous generations of this control based on provided list of all descendants controls.
   * A control can be reused in multiple other controls as a descendant.
   * Here the previous generation of this control will be these other controls.
   * Control A --> Descendant of Control B and Control C
   * Control B --> Descendant of Control D
   * Control C --> Descendant of Control E
   * All previous generations of Control A = [[Control B, Control D], [Control C, Control E]]
   *
   * @param controlUri
   * @param allDescendantsControls  Something like response of allDescendantsFlat()
   *  */
  public getGenerationsOf(controlUri: string, allDescendantsControls: Array<EntityConfigControlChild>): Array<Array<string>> {
    const parents = allDescendantsControls
      .filter((control) => controlUri === control.uri)
      .map((control) => {
        const parent = [];
        // How deep do you want to search for the parent Controls? Current use case seen upto 3 - 4 generations.
        // We cannot let it run for indefinite depth, in case like circular generation because of faulty migration.
        for (let i = 0; i < this.MAX_GENERATION_CAP; i++) {
          if (control && control.parentControlUri) {
            parent.push(control.parentControlUri);
            control = allDescendantsControls.find((ctrl) => control.parentControlUri === ctrl.uri);
          } else {
            break;
          }
        }
        return parent;
      });
    return parents;
  }

  // Get all descendants as flat array from given list of controls
  public allDescendantsFlat(allControls: Array<EntityConfigControl>): Array<EntityConfigControlChild> {
    return Array.from(new Set<EntityConfigControlChild>(allControls.reduce((prev, curr) => [...prev, ...curr.descendants], [])));
  }

  /**
   * Filters out controls with disabled feature flags
   *
   * @param controls
   * @param allEnabledFeatureFlags
   * @returns
   */
  public filterDisabledFeatureFlag(
    controls: Array<EntityConfigControl>,
    allEnabledFeatureFlags: Array<string>
  ): Array<EntityConfigControl> {
    return controls.filter((control) => {
      const featureFlag: string = control?.propertyValues?.find((propertyValue) =>
        propertyValue.controlTypePropertyUri.endsWith(PropertySuffix.FEATURE_FLAG)
      )?.value;
      if (featureFlag) {
        return allEnabledFeatureFlags.includes(featureFlag);
      }
      // If there is no a Feature flag we do not filter out!
      return true;
    });
  }

  /**
   * @deprecated This method is deprecated. Please use getElementTypesBasedOnPermission method from ElementConfigStateService instead.
   *
   * Return all the element types based on the provide actions
   *
   * @param actions - List of permission actions
   * @param spaceUri - space uri
   * @returns
   */
  public async getElementTypesBasedOnPermission(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    spaceUri?: string
  ): Promise<Array<EntityConfigType>> {
    const types: Array<EntityConfigType> = await this.getAllElementTypes(spaceUri);
    let filteredTypes: Array<EntityConfigType> = [];

    if (!types?.length) {
      return filteredTypes;
    }

    // Collect all permission check event
    const permissionObs: Array<Promise<boolean>> = types.map((type) =>
      firstValueFrom(this.permissionService.userHasElementTypePermissions(actions, type.uri, spaceUri))
    );

    // Trigger event and collect permission
    const permissionBooleanArray: Array<boolean> = await Promise.all(permissionObs);

    // Filter out all the types which doesn't have the permissions
    filteredTypes = types.filter((_type, index) => permissionBooleanArray[index]);

    return filteredTypes;
  }

  /**
   * @deprecated This method is deprecated. Please use getElementTypesBasedOnAnyPermission method from ElementConfigStateService instead.
   *
   * Return all the element types based on the either of the provided actions
   *
   * @param actions - List of permission actions
   * @param spaceUri - space uri
   * @returns
   */
  public async getElementTypesBasedOnAnyPermission(
    actions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>,
    spaceUri?: string
  ): Promise<Array<EntityConfigType>> {
    const types: Array<EntityConfigType> = await this.getAllElementTypes(spaceUri);
    let filteredTypes: Array<EntityConfigType> = [];

    if (!types?.length) {
      return filteredTypes;
    }

    // Collect all permission check event
    const permissionObs: Array<Promise<boolean>> = types.map((type) =>
      firstValueFrom(this.permissionService.userHasAnyElementTypePermissions(actions, type.uri, spaceUri))
    );

    // Trigger event and collect permission
    const permissionBooleanArray: Array<boolean> = await Promise.all(permissionObs);

    // Filter out all the types which doesn't have the permissions
    filteredTypes = types.filter((_type, index) => permissionBooleanArray[index]);

    return filteredTypes;
  }

  /**
   * @deprecated This method is deprecated. Please use getAllElementTypesBasedOnViewPermission method from ElementConfigStateService instead.
   *
   * Returns element types with view permission
   *
   * @param {string} spaceUri
   * @return {Promise<Array<EntityConfigType>>}
   * @memberof EntityConfigHelperService
   */
  public getAllElementTypesBasedOnViewPermission(spaceUri?: string): Promise<Array<EntityConfigType>> {
    return this.getElementTypesBasedOnAnyPermission(
      [PermissionVerbsV2.VIEW_ELEMENT, PermissionVerbsV2.VIEW_DRAFT_ELEMENT, PermissionVerbsV2.VIEW_ARCHIVED_ELEMENT],
      spaceUri
    );
  }

  /**
   * @deprecated This method is deprecated. Please use checkCreatePermissionForAllElementTypes method from ElementConfigStateService instead.
   *
   * Check if all element types have create element and create draft element permission
   *
   * Note: `getElementTypesBasedOnPermission` method returns all the elements with the permission
   * @if the method returns empty array, we assume that the permission is not there
   *
   * @returns
   */
  public async checkCreatePermissionForAllElementTypes(): Promise<boolean> {
    const elementTypes = await this.getElementTypesBasedOnAnyPermission([
      PermissionVerbsV2.CREATE_ELEMENT,
      PermissionVerbsV2.CREATE_DRAFT_ELEMENT
    ]);
    return !!elementTypes?.length;
  }
}
